import { useEffect, useRef, useState } from 'react'
import { sendSocket } from '../realtime/socket.ts'
import { useAuth } from '../store/auth.ts'
import { useChats } from '../store/chats.ts'
import { Avatar } from './Avatar.tsx'
import type { Message } from '../api/types.ts'
import { VoiceRecorder } from './VoiceRecorder.tsx'
import { EmojiPicker } from './EmojiPicker.tsx'
import { Icon } from './Icon.tsx'
import { caretAt, flatten, putCaret, readText, typeText, writeText } from '../lib/editable.ts'
import { PollDialog } from './PollDialog.tsx'
import { VideoRecorder } from './VideoRecorder.tsx'

const MAX_HEIGHT = 140
const MAX_FILES = 10

/** Должно совпадать с MAX_UPLOAD_MB на сервере. */
const MAX_FILE_MB = 25

type Picked = {
  file: File
  /** Ссылка на местный предпросмотр — только для картинок */
  preview: string | null
}

type ComposerProps = {
  chatId: string
  /** На какое сообщение отвечаем */
  replyTo: Message | null
  onCancelReply: () => void
  /** Какое сообщение правим */
  editing: Message | null
  onCancelEdit: () => void
}

export function Composer({ chatId, replyTo, onCancelReply, editing, onCancelEdit }: ComposerProps) {
  const send = useChats((s) => s.send)
  const sendFiles = useChats((s) => s.sendFiles)
  const editMessage = useChats((s) => s.editMessage)

  const [text, setText] = useState('')
  const [picked, setPicked] = useState<Picked[]>([])
  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [filming, setFilming] = useState(false)

  /**
   * ПОДСКАЗКА ПРИ НАБОРЕ «@»
   *
   * Зов по имени теперь значит многое: отдельная пометка в списке,
   * уведомление даже в заглушённой переписке. Значит, промахнуться
   * им нельзя — а промахнуться легко: сервер ищет первое слово имени
   * буква в букву, и «@Дима» вместо «@Дмитрий» просто не сработает,
   * причём молча.
   *
   * Поэтому имя не набирают, а выбирают.
   */
  const members = useChats((s) => (chatId ? s.detail[chatId]?.members : undefined))
  const me = useAuth((s) => s.user)
  const [hintAt, setHintAt] = useState<number | null>(null)
  const [hintPick, setHintPick] = useState(0)

  const areaRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingSentAt = useRef(0)
  /** Идёт набор через подсказки клавиатуры или иероглифы */
  const composing = useRef(false)
  /**
   * Где стоял курсор в последний раз.
   *
   * Стоит открыть выбор эмодзи — и поле теряет ввод вместе с курсором.
   * Вернув ввод, браузер ставит курсор в начало, и смайлик приезжал
   * в начало строки, а не туда, куда его ставили.
   */
  const lastCaret = useRef(0)
  /**
   * Полоска оформления над выделенным куском.
   *
   * Разметку мы разбираем при показе — жирный, курсив, скрытый текст
   * и прочее. Но набрать её было нечем: на телефоне нет ни Ctrl+B,
   * ни удобного способа поставить звёздочки вокруг слова. Получалось,
   * что показываем то, чего никто не пишет.
   */
  const [marking, setMarking] = useState<{ left: number; top: number } | null>(null)

  /**
   * Черновики. Недописанное переживает переход в другую переписку
   * и перезагрузку страницы: потерять набранный абзац из-за случайного
   * нажатия обиднее всего.
   *
   * Храним в браузере, а не на сервере: черновик — дело личное,
   * и на другом устройстве он не нужен.
   */
  const draftKey = 'family.draft.' + chatId

  useEffect(() => {
    if (editing) return
    try {
      // В поле текст перенесёт согласование ниже — здесь только
      // достаём его из хранилища
      const saved = localStorage.getItem(draftKey)
      if (saved) setText(saved)
    } catch {
      /* в приватном окне хранилище может быть закрыто */
    }
    // Правка идёт своим путём — её текст черновиком не считается
  }, [chatId])

  useEffect(() => {
    if (editing) return
    try {
      if (text.trim()) localStorage.setItem(draftKey, text)
      else localStorage.removeItem(draftKey)
    } catch {
      /* ничего не поделать */
    }
  }, [text, draftKey, editing])

  // Ссылки на предпросмотр держат файл в памяти, пока их не освободить
  useEffect(() => {
    return () => {
      for (const item of picked) if (item.preview) URL.revokeObjectURL(item.preview)
    }
  }, [picked])

  // Вошли в правку — подставляем текст и ставим курсор в конец
  useEffect(() => {
    if (!editing) return
    setText(editing.body ?? '')
    requestAnimationFrame(() => areaRef.current?.focus())
  }, [editing])

  /** Эмодзи вставляется туда, где стоит курсор, а не в конец строки. */
  /**
   * Обернуть выделенное знаками разметки.
   *
   * Вставкой, а не подменой поля: выделенное заменяется само, отмена
   * по Ctrl+Z работает, а состояние подтянется обычным ходом.
   */
  const wrapSelection = (mark: string) => {
    const area = areaRef.current
    const selection = window.getSelection()
    if (!area || !selection || selection.isCollapsed) return

    const chosen = selection.toString()
    if (!chosen) return

    area.focus()
    typeText(mark + chosen + mark)
    setMarking(null)
    onChange()
  }

  /** Показываем полоску, когда в поле что-то выделено. */
  const watchSelection = () => {
    const area = areaRef.current
    const selection = window.getSelection()

    if (
      !area ||
      !selection ||
      selection.isCollapsed ||
      selection.rangeCount === 0 ||
      !area.contains(selection.getRangeAt(0).commonAncestorContainer)
    ) {
      setMarking(null)
      return
    }

    const spot = selection.getRangeAt(0).getBoundingClientRect()
    const box = area.getBoundingClientRect()
    if (spot.width === 0 && spot.height === 0) {
      setMarking(null)
      return
    }

    // Считаем от строки ввода: полоска живёт рядом с ней, а не
    // в углу экрана
    setMarking({ left: spot.left - box.left + spot.width / 2, top: box.top - 10 })
  }

  const insertEmoji = (char: string) => {
    const area = areaRef.current
    if (!area) return

    // Возвращаем курсор туда, где он был до открытия выбора эмодзи,
    // и печатаем за человека: так сохраняется отмена по Ctrl+Z
    area.focus()
    putCaret(area, Math.min(lastCaret.current, readText(area).length))
    typeText(char)

    setText(readText(area))
    lastCaret.current = caretAt(area)
    grow()
  }

  /**
   * Геопозиция отправляется ссылкой на карты, а не встроенной картой:
   * встроенная тянула бы чужой код на страницу, а правило проекта —
   * ни одного запроса на сторону.
   */
  const shareLocation = () => {
    setAttachOpen(false)
    if (!navigator.geolocation) {
      setError('Браузер не умеет определять положение')
      return
    }

    setError(null)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        const lat = latitude.toFixed(6)
        const lon = longitude.toFixed(6)
        void send(
          chatId,
          `📍 Я здесь\nhttps://yandex.ru/maps/?pt=${lon},${lat}&z=17&l=map`,
          undefined,
          replyTo?.id,
        ).then(() => onCancelReply())
      },
      () => setError('Не удалось определить положение. Разреши доступ в настройках браузера.'),
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  /**
   * Поле растёт под текст, но не выше нескольких строк.
   *
   * Дальше внутри появляется прокрутка: длинное письмо не должно
   * съедать переписку целиком.
   */
  const grow = () => {
    const area = areaRef.current
    if (!area) return
    area.style.height = 'auto'
    area.style.height = Math.min(area.scrollHeight, MAX_HEIGHT) + 'px'
  }

  /**
   * Поле и состояние держим в согласии.
   *
   * Обычный набор ничего здесь не делает: на каждой букве состояние
   * уже равно содержимому поля. А вот когда текст меняем мы —
   * восстановили черновик, вошли в правку, очистили после отправки —
   * поле об этом само не узнает: React в его внутренности не лезет,
   * иначе на каждой букве сбрасывался бы курсор.
   *
   * Раньше каждое такое место переносило текст в поле само, и одно
   * из них — восстановление черновика — молча не срабатывало:
   * недописанное лежало в хранилище, состояние о нём знало, а в поле
   * было пусто.
   */
  useEffect(() => {
    const area = areaRef.current
    if (!area || composing.current) return
    if (readText(area) === text) return

    writeText(area, text)
    grow()
  }, [text])

  /**
   * Опустошить поле.
   *
   * Содержимое узла React за нас не перерисует: он про его внутренности
   * ничего не знает и знать не должен — иначе на каждой букве
   * сбрасывался бы курсор. Поэтому чистим и узел, и состояние.
   */
  const clearField = () => {
    setText('')
  }

  const addFiles = (incoming: FileList | File[]) => {
    setError(null)
    const list = [...incoming]
    const room = MAX_FILES - picked.length

    if (list.length > room) {
      setError(`За раз можно отправить не больше ${MAX_FILES} файлов`)
    }

    const accepted: Picked[] = []
    for (const file of list.slice(0, Math.max(room, 0))) {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setError(`«${file.name}» больше ${MAX_FILE_MB} МБ`)
        continue
      }
      accepted.push({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      })
    }

    if (accepted.length > 0) setPicked((current) => [...current, ...accepted])
  }

  const removeAt = (index: number) => {
    setPicked((current) => {
      const item = current[index]
      if (item?.preview) URL.revokeObjectURL(item.preview)
      return current.filter((_, i) => i !== index)
    })
  }

  const submit = async (when?: string) => {
    if (sending) return
    const body = text.trim()

    // Правка — отдельный путь: файлы туда не добавляются, а пустой текст
    // означал бы удаление, и это должно быть отдельным осознанным действием
    if (editing) {
      if (!body) return
      setSending(true)
      try {
        await editMessage(editing.id, body)
        clearField()
        onCancelEdit()
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Не удалось изменить')
      } finally {
        setSending(false)
      }
      return
    }

    if (!body && picked.length === 0) return

    setSending(true)
    setError(null)
    setProgress(0)

    // Поле очищаем сразу: ждать ответа сервера, чтобы начать писать
    // следующее сообщение, — раздражает
    const keptText = text
    const keptFiles = picked
    clearField()
    setPicked([])

    /**
     * Вложения уходят, не запирая поле ввода.
     *
     * Снимок с телефона уходит секунд десять, а по дороге на дачу —
     * и минуту. Всё это время нельзя было ни написать следующее, ни
     * понять, что происходит: только цифра над полем. Теперь сообщение
     * сразу стоит в ленте со своей полоской, а поле свободно.
     */
    if (keptFiles.length > 0) {
      setSending(false)
      onCancelReply()
      areaRef.current?.focus()

      void sendFiles(chatId, keptFiles.map((p) => p.file), body, setProgress)
        .catch(() => {
          // О неудаче скажет само сообщение в ленте: там же его и убирают
        })
        .finally(() => {
          for (const item of keptFiles) if (item.preview) URL.revokeObjectURL(item.preview)
          setProgress(0)
        })
      return
    }

    try {
      await send(chatId, body, undefined, replyTo?.id, when)
      onCancelReply()
    } catch (failure) {
      // Не отправилось — возвращаем всё на место, чтобы труд не пропал
      setText(keptText)
      setPicked(keptFiles)
      setError(failure instanceof Error ? failure.message : 'Не удалось отправить')
      requestAnimationFrame(grow)
    } finally {
      setSending(false)
      setProgress(0)
      areaRef.current?.focus()
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    /**
     * Пока открыта подсказка с именами, стрелки и Enter принадлежат ей.
     *
     * Иначе Enter отправит «@Дми» недописанным — ровно то, от чего
     * подсказка и должна спасать.
     */
    if (hints.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHintPick((at) => (at + 1) % hints.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHintPick((at) => (at - 1 + hints.length) % hints.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        putName(hints[hintPick]?.displayName ?? '')
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setHintAt(null)
        return
      }
    }

    /**
     * Привычные сочетания: жирный, курсив, зачёркнутый.
     *
     * На клавиатуре это быстрее любой полоски, а полоска остаётся
     * для телефона, где сочетаний нет.
     */
    if (event.ctrlKey || event.metaKey) {
      const key = event.key.toLowerCase()
      const mark =
        key === 'b' ? '*' : key === 'i' ? '_' : key === 'x' && event.shiftKey ? '~' : null

      if (mark) {
        event.preventDefault()
        wrapSelection(mark)
        return
      }
    }

    // Enter отправляет, Shift+Enter переносит строку — как принято
    // в мессенджерах. На телефоне Enter всегда переносит: там есть
    // отдельная кнопка отправки.
    const isTouch = window.matchMedia('(pointer: coarse)').matches
    if (event.key === 'Enter' && !event.shiftKey && !isTouch) {
      event.preventDefault()
      void submit()
      return
    }

    /**
     * Перенос строки ставим сами.
     *
     * Узел с разрешённым вводом на Enter заводит внутри себя абзацы
     * и переносы — у каждого браузера свои. Разбирать это потом
     * невозможно, поэтому вставляем обычный перенос в текст: поле
     * выводит его как строку, а на отправку уходит простой текст.
     */
    if (event.key === 'Enter') {
      event.preventDefault()
      typeText('\n')
      onChange()
    }
  }

  /**
   * Что сейчас набирают после «@».
   *
   * Собачка считается началом обращения только в начале слова: в почте
   * «kim@example» никого не зовут. Возвращаем место собачки и набранное
   * после неё — по нему и отбираем.
   */
  const askedAt = (value: string, caret: number): { from: number; typed: string } | null => {
    const before = value.slice(0, caret)
    const at = before.lastIndexOf('@')
    if (at < 0) return null

    const prev = at > 0 ? before[at - 1] : ' '
    if (prev && !/[\s(«"']/.test(prev)) return null

    const typed = before.slice(at + 1)
    // Пробел закончил обращение, да и имя в семье короткое
    if (/[\s]/.test(typed) || typed.length > 20) return null

    return { from: at, typed }
  }

  const asked = hintAt !== null ? { from: hintAt, typed: text.slice(hintAt + 1) } : null

  const hints =
    asked && members && members.length > 2
      ? members
          .filter((m) => m.id !== me?.id)
          .filter((m) =>
            m.displayName.toLowerCase().startsWith(asked.typed.toLowerCase()),
          )
          .slice(0, 6)
      : []

  /** Подставить выбранное имя вместо набранного куска. */
  const putName = (name: string) => {
    if (hintAt === null) return
    const area = areaRef.current
    if (!area) return

    const first = name.trim().split(/\s+/)[0] ?? name
    const caret = caretAt(area)
    const next = text.slice(0, hintAt) + '@' + first + ' ' + text.slice(caret)

    // Возвращаем место ввода за подставленное имя, а не в конец строки:
    // обращение часто стоит в начале, а дописывают дальше
    const to = hintAt + first.length + 2

    setText(next)
    setHintAt(null)
    setHintPick(0)

    // Здесь курсор важен: дописывают сразу за подставленным именем,
    // а не в конце строки. Согласование поставило бы его в конец
    writeText(area, next, to)
    area.focus()
    putCaret(area, to)
    grow()
  }

  const onChange = () => {
    const area = areaRef.current
    if (!area) return

    /**
     * Распрямляем поле, если браузер успел завести в нём свои узлы.
     *
     * Только не во время набора иероглифов и подсказок клавиатуры:
     * там содержимое меняет сама система, и вмешиваться в этот момент
     * значит стирать наполовину набранное слово.
     */
    if (!composing.current) flatten(area)

    const value = readText(area)
    setText(value)
    grow()

    lastCaret.current = caretAt(area)
    const found = askedAt(value, lastCaret.current)
    setHintAt(found ? found.from : null)
    setHintPick(0)

    const now = Date.now()
    if (now - typingSentAt.current > 2000) {
      typingSentAt.current = now
      sendSocket({ type: 'typing', chatId })
    }
  }

  /**
   * Открыть нужную дверь: галерею, камеру или файлы.
   *
   * Телефон выбирает её по «accept» и «capture» у поля, поэтому поле
   * одно, а настройки ставим перед нажатием.
   */
  const pick = (accept: string, capture?: string) => {
    const field = inputRef.current
    if (!field) return

    if (accept) field.setAttribute('accept', accept)
    else field.removeAttribute('accept')

    if (capture) field.setAttribute('capture', capture)
    else field.removeAttribute('capture')

    setAttachOpen(false)
    field.click()
  }

  /** Снимок экрана из буфера обмена вставляется как файл. */
  const onPaste = (event: React.ClipboardEvent) => {
    const files = [...event.clipboardData.files]
    if (files.length > 0) {
      event.preventDefault()
      addFiles(files)
      return
    }

    /**
     * Текст вставляем простым.
     *
     * Иначе вместе со скопированным со страницы приезжает его
     * оформление — чужие шрифты, цвета и ссылки прямо в строку ввода,
     * а на отправку всё равно уходит только текст.
     */
    const plain = event.clipboardData.getData('text/plain')
    if (plain) {
      event.preventDefault()
      typeText(plain)
      onChange()
    }
  }

  const canSend = (text.trim().length > 0 || picked.length > 0) && !sending

  const sendVoice = async (file: File, durationSec: number, waveform: number[]) => {
    setRecording(false)
    setSending(true)
    try {
      await sendFiles(chatId, [file], '', setProgress, { durationSec, waveform, kind: 'VOICE' })
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не удалось отправить')
    } finally {
      setSending(false)
      setProgress(0)
    }
  }

  if (recording) {
    return (
      <div className="composer-wrap">
        <VoiceRecorder onSend={(f, d, w) => void sendVoice(f, d, w)} onCancel={() => setRecording(false)} />
      </div>
    )
  }

  return (
    <div
      className="composer-wrap"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files)
      }}
    >
      {(replyTo || editing) && (
        <div className="reply-bar">
          <div className="reply-bar-body">
            <div className="reply-bar-title">
              {editing ? 'Изменить сообщение' : `Ответ ${replyTo?.author?.displayName ?? ''}`}
            </div>
            <div className="reply-bar-text">
              {(editing ?? replyTo)?.body ?? 'вложение'}
            </div>
          </div>
          <button
            className="icon-btn"
            onClick={() => {
              clearField()
              editing ? onCancelEdit() : onCancelReply()
            }}
            aria-label="Отменить"
          >
            ✕
          </button>
        </div>
      )}

      {attachOpen && (
        <div className="menu-overlay" onClick={() => setAttachOpen(false)}>
          <div className="attach-menu" onClick={(e) => e.stopPropagation()}>
            {/**
             * Галерея и файлы — разные двери.
             *
             * Раньше пункт был один, и телефон открывал по нему
             * файловый проводник: чтобы послать вчерашний снимок,
             * приходилось искать его среди папок. Отдельный пункт
             * с «image/*,video/*» открывает сразу галерею, а камера —
             * сразу камеру.
             */}
            <button className="msg-menu-item" onClick={() => pick('image/*,video/*')}>
              <Icon name="image" size={20} /> Фото или видео
            </button>
            <button className="msg-menu-item" onClick={() => pick('image/*,video/*', 'environment')}>
              <Icon name="camera" size={20} /> Снять
            </button>
            <button className="msg-menu-item" onClick={() => pick('')}>
              <Icon name="file" size={20} /> Файл
            </button>
            <button
              className="msg-menu-item"
              onClick={() => {
                setAttachOpen(false)
                setFilming(true)
              }}
            >
              <Icon name="round" size={20} /> Видеосообщение
            </button>
            <button className="msg-menu-item" onClick={shareLocation}>
              <Icon name="place" size={20} /> Геопозиция
            </button>
            <button
              className="msg-menu-item"
              onClick={() => {
                setAttachOpen(false)
                setPollOpen(true)
              }}
            >
              <Icon name="poll" size={20} /> Опрос
            </button>
          </div>
        </div>
      )}

      {filming && (
        <VideoRecorder
          onSend={(file, durationSec) => {
            setFilming(false)
            void sendFiles(chatId, [file], '', setProgress, { durationSec, waveform: [], kind: 'ROUND' })
          }}
          onCancel={() => setFilming(false)}
        />
      )}

      {emojiOpen && (
        <EmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
      )}

      {pollOpen && <PollDialog chatId={chatId} onClose={() => setPollOpen(false)} />}

      {scheduleOpen && (
        <div className="sheet" onClick={() => setScheduleOpen(false)}>
          <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <h2>Отправить позже</h2>
              <button className="icon-btn" onClick={() => setScheduleOpen(false)} aria-label="Закрыть">
                ✕
              </button>
            </div>

            <p className="sheet-text">
              Сообщение уйдёт в указанное время. До этого его увидишь только ты.
            </p>

            <label className="field">
              <span className="field-label">Когда</span>
              <input
                className="input"
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
              />
            </label>

            <button
              className="btn-primary"
              disabled={!scheduleAt}
              onClick={() => {
                const when = new Date(scheduleAt).toISOString()
                setScheduleOpen(false)
                setScheduleAt('')
                void submit(when)
              }}
            >
              Запланировать
            </button>
          </div>
        </div>
      )}

      {/**
       * Полоска оформления: жирный, курсив, зачёркнутый, моноширинный,
       * скрытый. Знаки разметки те же, что понимает показ сообщений.
       */}
      {marking && (
        <div
          className="mark-bar"
          style={{ left: marking.left }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('*')} title="Жирный (Ctrl+B)">
            <b>Ж</b>
          </button>
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('_')} title="Курсив (Ctrl+I)">
            <i>К</i>
          </button>
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('~')} title="Зачёркнутый">
            <s>З</s>
          </button>
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('`')} title="Моноширинный">
            <code>{'{}'}</code>
          </button>
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection('||')} title="Скрытый текст">
            <span className="mark-hidden">С</span>
          </button>
        </div>
      )}

      {/* Подсказка стоит над строкой ввода, как в привычных мессенджерах:
          набранное видно, а список не закрывает переписку целиком */}
      {hints.length > 0 && (
        <div className="mention-hints">
          {hints.map((m, index) => (
            <button
              key={m.id}
              className={'mention-hint' + (index === hintPick ? ' is-picked' : '')}
              onMouseDown={(event) => {
                // Именно нажатие, а не щелчок: щелчок успевает увести
                // место ввода из строки, и подставлять уже некуда
                event.preventDefault()
                putName(m.displayName)
              }}
            >
              <Avatar id={m.id} name={m.displayName} url={m.avatarUrl} size={28} />
              <span className="mention-hint-name">{m.displayName}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div className="composer-error">{error}</div>}

      {picked.length > 0 && (
        <div className="picked scroll">
          {picked.map((item, index) => (
            <div key={index} className="picked-item">
              {item.preview ? (
                <img src={item.preview} alt="" />
              ) : (
                <span className="picked-doc" title={item.file.name}>
                  📎 {item.file.name}
                </span>
              )}
              <button
                className="picked-remove"
                onClick={() => removeAt(index)}
                aria-label="Убрать файл"
                disabled={sending}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {sending && progress > 0 && (
        <div className="upload-bar" role="progressbar" aria-valuenow={progress}>
          <div className="upload-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className="composer">
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            // Сбрасываем, иначе второй выбор того же файла не сработает
            e.target.value = ''
          }}
        />

        <button
          className="composer-btn"
          onClick={() => setAttachOpen((v) => !v)}
          title="Прикрепить"
          aria-label="Прикрепить"
          disabled={sending}
        >
          📎
        </button>

        <button
          className="composer-btn"
          onClick={() => setEmojiOpen(true)}
          title="Эмодзи"
          aria-label="Эмодзи"
          disabled={sending}
        >
          🙂
        </button>

        {/**
         * Не «textarea», а узел с разрешённым вводом.
         *
         * Браузер на телефоне пристраивает над клавиатурой свою полосу
         * со стрелками «предыдущее поле», «следующее поле» и галочкой
         * «готово»: она нужна для анкет, где полей много. В переписке
         * прыгать некуда — полоса просто занимала место между строкой
         * ввода и клавиатурой. Узел с разрешённым вводом браузеры
         * анкетой не считают и полосу не показывают. Так же устроены
         * все веб-мессенджеры, и по той же причине.
         */}
        <div
          ref={areaRef}
          className="composer-input"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Написать сообщение"
          data-placeholder={
            picked.length > 0 ? 'Подпись (не обязательно)' : 'Написать сообщение…'
          }
          onInput={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onKeyUp={() => {
            const area = areaRef.current
            if (area) lastCaret.current = caretAt(area)
            watchSelection()
          }}
          onMouseUp={() => {
            const area = areaRef.current
            if (area) lastCaret.current = caretAt(area)
            watchSelection()
          }}
          onTouchEnd={() => {
            // Выделение пальцем заканчивается позже отпускания:
            // системные ручки ставятся своим чередом
            window.setTimeout(watchSelection, 120)
          }}
          onBlur={() => {
            const area = areaRef.current
            if (area) lastCaret.current = caretAt(area)
          }}
          onCompositionStart={() => {
            composing.current = true
          }}
          onCompositionEnd={() => {
            composing.current = false
            onChange()
          }}
        />

        {canSend || editing ? (
          <button
            className={'composer-send' + (canSend ? ' is-active' : '')}
            onClick={() => void submit()}
            onContextMenu={(event) => {
              event.preventDefault()
              if (canSend) setScheduleOpen(true)
            }}
            disabled={!canSend}
            title="Отправить. Правой кнопкой — отправить позже"
            aria-label="Отправить"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        ) : (
          /* Пока писать нечего, на месте отправки — микрофон:
             так в привычных мессенджерах, и лишней кнопки не нужно */
          <button
            className="composer-send"
            onClick={() => setRecording(true)}
            disabled={sending}
            title="Записать голосовое"
            aria-label="Записать голосовое"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
